import classNames from "classnames";
import { ActionDiv } from "PFComponents/containers";
import { ExperienceLevel, ExperienceLevelProps } from "PFComponents/experience_level";
import { ParentChildCommonProps } from "PFComponents/experience_level/experience_level.types";
import { Icon } from "PFComponents/icon";
import { Typography } from "PFComponents/typography";
import { CustomValueTag, Experience } from "PFTypes";
import React, { AriaAttributes, PropsWithChildren, ReactNode, useMemo } from "react";

import css from "./custom_value_pill.module.scss";
import { getSmartMaxNameLength } from "./custom_value_pill.utils";
import { useCustomValueAriaLabel } from "./hooks/use_custom_value_aria_label";
import { TagsGroupedIcon } from "./parts/tags_grouped_icon";
import { CustomValuePillTooltip } from "./parts/tooltip/custom_value_pill_tooltip";
import { CustomValueTooltipElement } from "./parts/tooltip/custom_value_pill_tooltip.types";
import { VerifiedGroupedIcon } from "./parts/verified_grouped_icon";

export type CustomValuePillProps = {
  name: string;
  experienceLevels: Experience[];
  experienceLabelMap: Record<Experience, string>;
  customTypeAriaLabel?: string;
  experience?: Experience;
  requiredExperience?: Experience;
  tags?: CustomValueTag[];
  isCore?: boolean;
  isDevelopmental?: boolean;
  isVerified?: boolean;
  isVerifiedCredly?: boolean;
  isVerifiedOthers?: boolean;
  isRequirement?: boolean;
  isPendingApproval?: boolean;
  maxNameLength?: number;
  onClick?: VoidFunction;
  experienceTooltipDisabled?: boolean;
  customLeftIconName?: "check" | "cross";
  customLeftContent?: ReactNode;
  customRightContent?: ReactNode;
  customTooltipElement?: ReactNode;
  tooltipElementsBlacklist?: CustomValueTooltipElement[];
  tabIndex?: number;
  className?: string;
} & Pick<ExperienceLevelProps, "theme" | "showTooltip"> &
  ParentChildCommonProps;

const ClickableWrapper = ({
  children,
  onClick,
  "aria-label": ariaLabel,
  tabIndex
}: PropsWithChildren<Pick<CustomValuePillProps, "onClick" | "tabIndex">> & AriaAttributes) => (
  <ActionDiv onClick={onClick} tabIndex={tabIndex ?? 0} aria-label={ariaLabel} className={css.actionDiv}>
    {children}
  </ActionDiv>
);

export const CustomValuePill = ({
  className,
  name,
  experienceLevels,
  experienceLabelMap,
  customTypeAriaLabel,
  tags,
  isCore,
  isDevelopmental,
  isVerified,
  isVerifiedCredly,
  isVerifiedOthers,
  isRequirement,
  isPendingApproval,
  maxNameLength = 40,
  onClick,
  theme,
  experience,
  requiredExperience,
  hasParentValues,
  hasChildValues,
  showTooltip = true,
  customLeftIconName,
  customLeftContent,
  customRightContent,
  customTooltipElement,
  tooltipElementsBlacklist,
  tabIndex
}: CustomValuePillProps) => {
  const ariaLabel = useCustomValueAriaLabel({
    name,
    customTypeAriaLabel,
    isRequirement,
    experience,
    requiredExperience,
    experienceLabelMap
  });
  const isClickable = !!onClick;
  const Wrapper = useMemo(() => (isClickable ? ClickableWrapper : React.Fragment), [isClickable]);

  const smartMaxNameLength = getSmartMaxNameLength({
    maxNameLength,
    isCore,
    isDevelopmental,
    isVerified,
    isVerifiedOthers,
    hasTags: (tags || []).length > 0
  });
  const isNameTooLong = name.length > smartMaxNameLength;

  return (
    <CustomValuePillTooltip
      showTooltip={showTooltip}
      experienceLevels={experienceLevels}
      experienceLabelMap={experienceLabelMap}
      experience={experience}
      requiredExperience={requiredExperience}
      hasParentValues={hasParentValues}
      hasChildValues={hasChildValues}
      isRequirement={isRequirement}
      isVerified={isVerified}
      isVerifiedCredly={isVerifiedCredly}
      isVerifiedOthers={isVerifiedOthers}
      isCore={isCore}
      isDevelopmental={isDevelopmental}
      isPendingApproval={isPendingApproval}
      tags={tags}
      customTooltipElement={customTooltipElement}
      tooltipElementsBlacklist={tooltipElementsBlacklist}
    >
      <Wrapper {...(isClickable ? { onClick, "aria-label": ariaLabel, tabIndex } : {})}>
        <span
          className={classNames(css.customValuePill, { [css.pendingApproval]: isPendingApproval }, className)}
        >
          {customLeftContent}
          {(experience || requiredExperience) && (
            <ExperienceLevel
              theme={theme}
              isRequirement={isRequirement}
              experience={experience}
              requiredExperience={requiredExperience}
              hasChildValues={hasChildValues}
              hasParentValues={hasParentValues}
              showTooltip={false}
              experienceLevels={experienceLevels}
              experienceLabelMap={experienceLabelMap}
            />
          )}
          {customLeftIconName && (
            <Icon
              name={customLeftIconName}
              color={customLeftIconName === "cross" ? "paletteRed0" : undefined}
              size="sm"
            />
          )}
          <div className={css.nameWrapper} title={name}>
            <Typography
              variant="labelButton"
              className={classNames(css.name, { [css.darkTheme]: theme === "dark" })}
            >
              {name.slice(0, smartMaxNameLength)}
              {isNameTooLong && "..."}
            </Typography>
          </div>
          <TagsGroupedIcon tags={tags} theme={theme} />
          {isCore && <Icon name="core" size="sm" />}
          {isDevelopmental && <Icon name="development" size="sm" />}
          <VerifiedGroupedIcon
            isVerified={isVerified}
            isVerifiedCredly={isVerifiedCredly}
            isVerifiedOthers={isVerifiedOthers}
            theme={theme}
          />
          {customRightContent}
        </span>
      </Wrapper>
    </CustomValuePillTooltip>
  );
};
